import { AREAS, GOAL_BOOST, STUDY_PLAN_EXAM_DATE } from '../config'
import { resolveSubject, buildSubjectLessonIndex, type SubjectLessonIndex } from '../resolve'
import type { Lesson, StudentProgress, Subject, Topic } from '@/types/index'
import { currentPhase, monthsLeft, PHASE_LABELS, type Phase } from './phases'
import { assembleDay } from './dayBuilder'
import { dueTopics, todayISO } from './spacing'
import { ENGINE_VERSION_V2, type CheckInV2, type PlanStateV2, type PlanWeekV2, type TopicProgressV2 } from './types'

export { ENGINE_VERSION_V2 }
export { currentPhase, monthsLeft, PHASE_LABELS }
export { dueTopics, todayISO }
export type { Phase, PlanStateV2, PlanWeekV2, TopicProgressV2, CheckInV2 }

export type ProgressStatus = 'not_started' | 'in_progress' | 'completed'

export interface EngineV2Input {
  state: PlanStateV2
  subjects: Subject[]
  topics: Topic[]
  lessons: Lesson[]
  progress: StudentProgress[]
  topicsProgress: TopicProgressV2[]
  weekStart: string
  checkIns?: Record<string, CheckInV2>
  doneByDate?: Record<string, Set<number>>
}

export function cycleFor(state: PlanStateV2): string[] {
  const area = AREAS[state.area]
  const boost = GOAL_BOOST[state.goal] || []
  return [...boost, ...area.cycle]
}

function makeIndex(input: EngineV2Input): SubjectLessonIndex {
  return buildSubjectLessonIndex(input.subjects, input.topics, input.lessons)
}

function makeProgressMap(input: EngineV2Input): Map<number, string> {
  const map = new Map<number, string>()
  input.progress.forEach((p) => {
    if (p.status === 'in_progress' || p.status === 'completed') map.set(p.lesson_id, p.status)
  })
  return map
}

export function supplyFor(index: SubjectLessonIndex, progress: Map<number, string>) {
  return (subject: string) => {
    const r = resolveSubject(index, progress as Map<number, 'not_started' | 'in_progress' | 'completed'>, subject)
    if (!r) return { ours: false, lessonId: null, lessonTitle: null }
    const target = r.lessonId ?? r.gapLessonId
    const title = r.title ?? r.gapTitle
    if (!target) return { ours: false, lessonId: null, lessonTitle: null }
    return { ours: true, lessonId: target, lessonTitle: title }
  }
}

export function dayIdxFor(dateISO: string, weekStart: string): number {
  const a = new Date(dateISO + 'T12:00:00').getTime()
  const b = new Date(weekStart + 'T12:00:00').getTime()
  return Math.round((a - b) / 86400000)
}

export function isWeekendDate(dateISO: string): boolean {
  const d = new Date(dateISO + 'T12:00:00').getDay()
  return d === 0 || d === 6
}

function baseDayInput(input: EngineV2Input) {
  const phase = currentPhase(STUDY_PLAN_EXAM_DATE)
  return {
    phase,
    dailyMinutes: input.state.dailyMinutes,
    kelimeEnabled: input.state.kelime,
    dayStart: input.state.dayStart ?? null,
    cycle: cycleFor(input.state),
    supply: supplyFor(makeIndex(input), makeProgressMap(input)),
    topics: input.topicsProgress,
  }
}

export function buildDayV2(input: EngineV2Input, dateISO: string) {
  const base = baseDayInput(input)
  const done = input.doneByDate?.[dateISO]
  const doneKeys = done ? new Set(Array.from(done).map((i) => `${dateISO}#${i + 1}`)) : undefined
  return assembleDay({
    ...base,
    date: dateISO,
    dayIdx: dayIdxFor(dateISO, input.weekStart),
    isWeekend: isWeekendDate(dateISO),
    checkIn: input.checkIns?.[dateISO],
    doneKeys,
  })
}

export function buildWeekV2(input: EngineV2Input): PlanWeekV2 {
  const week: PlanWeekV2 = []
  for (let i = 0; i < 7; i++) {
    const d = new Date(input.weekStart + 'T12:00:00')
    d.setDate(d.getDate() + i)
    week.push(buildDayV2(input, todayISO(d)))
  }
  return week
}

export interface WeekStatsV2 {
  video: number
  soru: number
  deneme: number
  tekrar: number
  coreMins: number
  totalMins: number
}

export function weekStats(week: PlanWeekV2): WeekStatsV2 {
  const s: WeekStatsV2 = { video: 0, soru: 0, deneme: 0, tekrar: 0, coreMins: 0, totalMins: 0 }
  week.forEach((d) =>
    d.blocks.forEach((b) => {
      s.totalMins += b.mins
      if (b.core) s.coreMins += b.mins
      if (b.kind === 'video') s.video++
      if (b.kind === 'soru') s.soru++
      if (b.kind === 'deneme') s.deneme++
      if (b.kind === 'tekrar') s.tekrar++
    })
  )
  return s
}

export function planMetaV2(state: PlanStateV2) {
  const phase = currentPhase(STUDY_PLAN_EXAM_DATE)
  return {
    engineVersion: ENGINE_VERSION_V2,
    phase,
    phaseLabel: PHASE_LABELS[phase],
    countdown: Math.ceil(monthsLeft(STUDY_PLAN_EXAM_DATE) * 30),
    areaLabel: AREAS[state.area].label,
  }
}
